import { Template, Component, Attribute } from '@scoutgg/ftw/source/lib'
import template from './sc-validator.jade'
import { Match } from '../../models'

// SC Validator — V4 (stage navigator + auto-suggestions).
//   Stage 1 — Lineups validation
//   Stage 2 — Timeline review
//   Stage 3 — Anomaly detection
//   Stage 4 — Finalization (capture evidence + Confirm hand-off)
// Each stage shows count badge from validator's report.stages.suggestions[]
// filtered by stage. Suggestions surface an action + fix hint inline so the
// operator can resolve in one place. The component re-fetches the validator
// when match content mutates (debounced) so the panel reflects edits live.

const STAGES = [
  { idx: 1, key: 'lineups',  short: 'Lineups',     long: 'Stage 1 · Lineups validation' },
  { idx: 2, key: 'timeline', short: 'Timeline',    long: 'Stage 2 · Timeline review' },
  { idx: 3, key: 'anomalies',short: 'Anomalies',   long: 'Stage 3 · Anomaly detection' },
  { idx: 4, key: 'keystats', short: 'Key Stats',   long: 'Stage 4 · Shots, Saves, Penalties' },
  { idx: 5, key: 'finalize', short: 'Finalize',    long: 'Stage 5 · Capture & confirm' },
]
// Backwards compat: jade conditionals using activeStage === 4 still mean finalize
// at idx 5 now; we add idx 4 (keystats) inserted between anomalies and finalize.

// 12-step pipeline metadata for the at-a-glance bead bar (kept from V3 so
// operators see the full SC workflow state without clicking through stages).
const PIPELINE = [
  { actor: 'ai',       label: 'Verify match status & final score' },
  { actor: 'operator', label: 'Scrape feeds & Send events' },
  { actor: 'operator', label: 'Lock feeds' },
  { actor: 'operator', label: 'Refresh Live Results' },
  { actor: 'ai',       label: 'Lineup validation' },
  { actor: 'ai',       label: 'Stats validation' },
  { actor: 'ai',       label: 'Edge cases' },
  { actor: 'ai',       label: 'Anomaly detection' },
  { actor: 'ai',       label: 'Conflict resolution' },
  { actor: 'human',    label: 'Documentation' },
  { actor: 'human',    label: 'Human match confirmation' },
  { actor: 'human',    label: 'Log completion in #sc-log' },
]
const AI_IDX = [0, 4, 5, 6, 7, 8]

const STAGING_HOST = 'statcenter.scoutgg-stg.net'
const STAGING_VALIDATOR = 'https://sc-validator.axelio.io'
function validatorBase() {
  if (typeof window !== 'undefined' && window.__SC_VALIDATOR_BASE__) {
    return String(window.__SC_VALIDATOR_BASE__).replace(/\/$/, '')
  }
  if (typeof window !== 'undefined' && window.location && window.location.hostname === STAGING_HOST) {
    return STAGING_VALIDATOR
  }
  return '/sc-validator'
}

// Persisted sidebar open/closed preference. Global (not per-match) so the
// rail stays the operator's chosen default across navigations.
function loadOpenPref() {
  if (typeof localStorage === 'undefined') return false
  try { return localStorage.getItem('sc-val-open') === '1' } catch (_e) { return false }
}
function saveOpenPref(open) {
  if (typeof localStorage === 'undefined') return
  try { localStorage.setItem('sc-val-open', open ? '1' : '0') } catch (_e) {}
}

function loadDismissed(matchId) {
  if (typeof localStorage === 'undefined' || !matchId) return {}
  try { return JSON.parse(localStorage.getItem('sc-val-dismiss-' + matchId) || '{}') || {} }
  catch (_e) { return {} }
}
function saveDismissed(matchId, obj) {
  if (typeof localStorage === 'undefined' || !matchId) return
  try { localStorage.setItem('sc-val-dismiss-' + matchId, JSON.stringify(obj)) } catch (_e) {}
}

@Component('sporty')
@Template(template)
@Attribute('match', Match)
export default class ScValidator extends HTMLElement {
  created() {
    // Sidebar rail-by-default. The 380px panel is opt-in via the expand
    // button on the rail edge. State persists across sessions/matches so
    // operators who prefer it open keep it open.
    this.open = loadOpenPref()
    // Set body class as early as possible so CSS can scope on it before
    // first paint. Otherwise v1 / v2 overlap during the initial mount.
    try {
      if (typeof document !== 'undefined' && document.body) {
        document.body.classList.toggle('sc-val-open', !!this.open)
        document.body.classList.toggle('sc-val-rail', !this.open)
      }
    } catch (_e) {}
    this.opChecks = {}
    this.loading = false
    this.error = null
    this.report = null
    this.ranFor = null
    this.activeStage = 1
    this.dismissed = {}
    this.capturing = false
    this.capture = null
    this.confirmed = false
    this._dirty = null   // debounce timer for live re-validate
    this._lastSig = ''   // signature of match content to detect edits
  }

  attached() {
    this.applyBodyClass()
    if (!this._reparentObs && typeof MutationObserver !== 'undefined') {
      const node = this
      const enforce = () => {
        try {
          if (node.parentNode && node.parentNode !== document.body) {
            document.body.appendChild(node)
          }
        } catch (_e) {}
      }
      enforce()
      const obs = new MutationObserver(enforce)
      obs.observe(document.body, { childList: true, subtree: false })
      this._reparentObs = obs
    }
    // Manual-only trigger — Ivan's request to reduce token usage.
    // Validator never auto-fires on mount or via polling. Operator must
    // click Re-run All to trigger a check.
    this.ranFor = null
    // Polling disabled by request — was firing every 30s consuming scraper-budget.
    // Escape key closes the open drawer.
    if (!this._escHandler) {
      this._escHandler = (ev) => {
        if (this.lightboxIndex != null) {
          if (ev.key === 'Escape') { this.closeLightbox(); ev.preventDefault(); ev.stopPropagation(); return }
          if (ev.key === 'ArrowRight') { this.lightboxNext(); ev.preventDefault(); ev.stopPropagation(); return }
          if (ev.key === 'ArrowLeft') { this.lightboxPrev(); ev.preventDefault(); ev.stopPropagation(); return }
        }
        if (ev.key === 'Escape' && this.open) {
          this.toggle()
          ev.preventDefault()
          ev.stopPropagation()
        }
      }
      document.addEventListener('keydown', this._escHandler, true)
    }
    this.maybeRun()
  }

  detached() {
    if (this._dirty) { clearTimeout(this._dirty); this._dirty = null }
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null }
    if (this._tickTimer) { clearInterval(this._tickTimer); this._tickTimer = null }
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler, true)
      this._escHandler = null
    }
    if (this._reparentObs) {
      this._reparentObs.disconnect()
      this._reparentObs = null
    }
  }

  applyBodyClass() {
    try {
      const b = document.body
      if (b) {
        b.classList.toggle('sc-val-open', !!this.open)
        b.classList.toggle('sc-val-rail', !this.open)
      }
      this.setAttribute('data-sc-state', this.open ? 'open' : 'rail')
      // Force position:fixed via inline style — CSS rules lose to FTW's
      // component-scoped styles for the host element, but inline style wins.
      const s = this.style
      if (this.open) {
        s.cssText =
          'position:fixed!important;top:0!important;left:0!important;right:0!important;bottom:0!important;' +
          'width:100vw!important;height:100vh!important;z-index:99999!important;' +
          'display:block!important;margin:0!important;padding:0!important;' +
          'transform:translateX(0)!important;transition:transform .22s ease;'
      } else {
        s.cssText =
          'position:fixed!important;top:110px!important;left:0!important;bottom:16px!important;' +
          'width:32px!important;height:auto!important;z-index:60!important;display:block!important;' +
          'transform:translateX(0)!important;transition:transform .22s ease, width .22s ease;'
      }
    } catch (_e) {}
  }

  matchChange() {
    this.maybeRun()
  }

  get matchId() {
    const m = this.match
    if (m) return m._id || m.id || null
    // Fallback: parse from URL when host was reparented (Match attribute lost).
    try {
      const path = (typeof location !== 'undefined' && location.pathname) || ''
      const mm = path.match(/\/dashboard\/([a-f0-9]{24})/i)
      if (mm) return mm[1]
    } catch (_e) {}
    return null
  }

  // ── live re-validation on operator edits ─────────────────────────────

  matchSignature() {
    const m = this.match
    if (!m) return ''
    const events = (m.events || []).map((e) => (e.name || '') + '/' + (e.eventTime || e.minute || 0) + '/' + (e.player || '')).join('|')
    const lineups = (m.lineups || []).map((l) => (l.player && l.player._id) + '/' + (l.status || '') + '/' + (l.duration || 0)).join('|')
    const score = (m.score || []).join('-')
    return events + '#' + lineups + '#' + score
  }

  maybeRun() {
    const id = this.matchId
    if (!id) return
    const sig = this.matchSignature()
    // First load on new match: full run.
    if (id !== this.ranFor) {
      this.ranFor = id
      this._lastSig = sig
      this.applyState({ dismissed: loadDismissed(id), activeStage: 1, capture: null, confirmed: false })
      this.run(id)
      return
    }
    // Same match, content changed — debounced re-run (live update).
    if (sig !== this._lastSig) {
      this._lastSig = sig
      if (this._dirty) clearTimeout(this._dirty)
      this._dirty = setTimeout(() => this.run(id), 500)
    }
  }

  async run(id, opts) {
    const base = validatorBase()
    const force = opts && opts.force
    // ETA: cold cross-check ≈70-90s (pulselive 5s + FPL 5s + WhoScored
    // 30-60s stealth-Chromium, all parallel). Cached call ≈<1s.
    // Client timeout = server max scraper timeout (45s) + buffer = 120s.
    const startedAt = Date.now()
    this.applyState({ loading: true, error: null, validatingStartedAt: startedAt, validatingEtaSec: 90 })
    // Tick the elapsed counter every second so the button shows progress.
    if (this._tickTimer) clearInterval(this._tickTimer)
    this._tickTimer = setInterval(() => {
      if (!this.loading) { clearInterval(this._tickTimer); this._tickTimer = null; return }
      this.applyState({ _tick: Date.now() })
    }, 1000)
    try {
      const ctl = new AbortController()
      // Force re-runs do a full cold cross-check (all 6 scrapers, Opta via
      // proxy takes 60-90s alone) — needs more headroom than warm-cache calls.
      const to = setTimeout(() => ctl.abort(), force ? 300000 : 120000)
      console.log('[sc-validator] fetching', base + '/validator/match/' + id)
      const res = await fetch(`${base}/validator/match/${encodeURIComponent(id)}?t=${Date.now()}${force ? '&force=1' : ''}`, {
        signal: ctl.signal,
        headers: { accept: 'application/json' },
        cache: 'no-store',
      })
      clearTimeout(to)
      const ms = Date.now() - startedAt
      console.log('[sc-validator] response', res.status, res.ok, `(${ms}ms)`)
      const rep = await res.json()
      console.log('[sc-validator] report', { stages: rep.stages ? Object.keys(rep.stages) : 'none', lineupDiffs: rep.stages?.lineupDiffs?.length, timelineDiffs: rep.stages?.timelineDiffs?.length, externalSource: rep.externalSource, ms })
      if (this._tickTimer) { clearInterval(this._tickTimer); this._tickTimer = null }
      this.applyState({ loading: false, report: rep, error: null, lastFetchMs: ms })
    } catch (e) {
      if (this._tickTimer) { clearInterval(this._tickTimer); this._tickTimer = null }
      this.applyState({
        loading: false,
        error: (e && e.message) || 'validator unreachable',
      })
    }
  }

  setLineupTab(tab) {
    this.applyState({ lineupTab: tab })
  }

  setTimelineTab(tab) {
    this.applyState({ timelineTab: tab })
  }

  setNotes(value) {
    this.applyState({ notes: value })
  }

  // Expose validatorBase() to the jade template so it uses the SAME
  // resolution (staging-host detection + same-origin /sc-validator fallback)
  // instead of just reading window.__SC_VALIDATOR_BASE__ and falling through
  // to an empty string (which makes shot URLs resolve relative to the host
  // statcenter.scoutgg-stg.net → 404 instead of axelio.io).
  get validatorBase() {
    return validatorBase()
  }
  get validatingElapsedSec() {
    if (!this.loading || !this.validatingStartedAt) return 0
    return Math.floor((Date.now() - this.validatingStartedAt) / 1000)
  }
  get validatingProgressPct() {
    const eta = this.validatingEtaSec || 90
    const elapsed = this.validatingElapsedSec
    return Math.min(99, Math.round((elapsed / eta) * 100))
  }
  formatSec(s) {
    const m = Math.floor(s / 60), r = s % 60
    return `${m}:${String(r).padStart(2, '0')}`
  }

  get lightboxList() {
    const base = validatorBase()
    const rep = this.report
    const plId = rep?.provenance?.external?.eventId || null
    const prov = rep?.provenance?.sportyman || {}
    const label = rep?.matchLabel || ''
    const homeName = prov.homeTeam || (label.split(/\s+\d/)[0] || '').trim()
    const m = label.match(/\d\s+(.+?)(\s+·|$)/)
    const awayName = prov.awayTeam || (m && m[1] || '').trim()
    const startTime = prov.startTime
    const date = startTime ? new Date(startTime).toISOString().slice(0, 10) : null
    const out = []
    if (plId) {
      const v = [['PL · Summary','summary'],['PL · Lineups','lineups'],['PL · Events','timeline'],['PL · Stats','stats']]
      v.forEach(([lbl, view]) => out.push({ label: lbl, url: `${base}/shot/${plId}/${view}` }))
    }
    if (homeName && awayName && date) {
      const v = [['FotMob · Summary','summary'],['FotMob · Lineups','lineups'],['FotMob · Events','timeline'],['FotMob · Stats','stats']]
      v.forEach(([lbl, view]) => out.push({ label: lbl, url: `${base}/shot-fm/${encodeURIComponent(homeName)}/${encodeURIComponent(awayName)}/${date}/${view}` }))
    }
    if (homeName && awayName && date) {
      const wsViews = [['WhoScored · Summary','summary'],['WhoScored · Live (events)','live'],['WhoScored · Lineups','lineups'],['WhoScored · Stats','stats']]
      wsViews.forEach(([lbl, view]) => out.push({ label: lbl, url: `${base}/shot-ws/${encodeURIComponent(homeName)}/${encodeURIComponent(awayName)}/${date}/${view}` }))
    }
    if (homeName && awayName) {
      const sofaViews = [['SofaScore · Summary','summary'],['SofaScore · Lineups','lineups'],['SofaScore · Events','events'],['SofaScore · Stats','stats']]
      const sofaDate = date || '2000-01-01'
      sofaViews.forEach(([lbl, view]) => out.push({ label: lbl, url: `${base}/shot-sofa/${encodeURIComponent(homeName)}/${encodeURIComponent(awayName)}/${sofaDate}/${view}` }))
    }
    if (homeName && awayName && date) {
      out.push({ label: 'Opta · Match detail', url: `${base}/shot-opta/${encodeURIComponent(homeName)}/${encodeURIComponent(awayName)}/${date}/match` })
    }
    out.push({ label: 'FPL · Fixtures', url: `${base}/shot-src/fpl` })
    return out
  }

  openLightboxAt(index) {
    this.applyState({ lightboxIndex: index })
  }

  closeLightbox() {
    this.applyState({ lightboxIndex: null })
  }

  lightboxPrev(e) {
    if (e) { e.stopPropagation(); e.preventDefault() }
    const i = this.lightboxIndex
    if (i == null || i <= 0) return
    this.applyState({ lightboxIndex: i - 1 })
  }

  lightboxNext(e) {
    if (e) { e.stopPropagation(); e.preventDefault() }
    const i = this.lightboxIndex
    const list = this.lightboxList
    if (i == null || i >= list.length - 1) return
    this.applyState({ lightboxIndex: i + 1 })
  }

  stopProp(e) {
    if (e) e.stopPropagation()
  }

  rerun() {
    const id = this.matchId
    if (!id) {
      this.applyState({ error: 'No match-id resolved — open a /dashboard/<id> page first.' })
      return
    }
    // Force fresh fetch even if same match (bypass ranFor cache + daemon cache).
    this.ranFor = null
    this.run(id, { force: true })
  }

  // Kopiera nuvarande dashboard-URL till clipboard så SC-teamet kan länka
  // direkt till matchen + öppna SC Validator hos en annan operatör. Visuell
  // bekräftelse via `shareCopied`-flagga som auto-reset:as efter 1.8 s.
  async share() {
    try {
      const url = (typeof location !== 'undefined') ? location.href : ''
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        const ta = document.createElement('textarea')
        ta.value = url
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand('copy') } catch (_e) { /* legacy fallback */ }
        document.body.removeChild(ta)
      }
      this.applyState({ shareCopied: true })
      setTimeout(() => this.applyState({ shareCopied: false }), 1800)
    } catch (e) {
      this.applyState({ error: 'Kunde inte kopiera länk: ' + ((e && e.message) || e) })
    }
  }

  toggle() {
    const next = !this.open
    saveOpenPref(next)
    this.applyState({ open: next })
    this.applyBodyClass()
  }
  setStage(idx) {
    this.applyState({ activeStage: idx })
    // Scroll to the corresponding panel in the v2 fullscreen layout.
    try {
      const root = this.shadowRoot || this
      const el = root.querySelector && root.querySelector('#sc-v2-stage-' + idx)
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (_e) {}
  }

  dismissSuggestion(id) {
    const next = Object.assign({}, this.dismissed || {})
    next[id] = true
    saveDismissed(this.matchId, next)
    this.applyState({ dismissed: next })
  }

  // ── verdict / counts ──────────────────────────────────────────────

  get verdictState() {
    const rep = this.report
    const v = rep && rep.summary && rep.summary.overallVerdict
    // Stage-shaped issues count (new pipeline) — must influence the verdict.
    // Otherwise we'd say "looks clean" while 14 stage suggestions are open.
    const issues = this.totalIssues || 0
    const critFromStages = ((rep && rep.stages && rep.stages.suggestions) || []).filter((s) => s.severity === 'critical').length

    // Synthetic / test fixture — distinguish from genuine "no source available"
    if (rep && rep.external_unavailable) {
      const reason = (rep.provenance && rep.provenance.external && rep.provenance.external.reason) || ''
      if (/NO_REAL_MATCH|synthetic|test/i.test(reason)) {
        return { tone: 'info', word: 'Synthetic fixture', sub: 'No real-world counterpart — cross-check skipped. Use prod for full validation.' }
      }
      return { tone: 'warn', word: 'Needs human review', sub: 'External cross-check unavailable.' }
    }

    if (critFromStages > 0) return { tone: 'danger', word: 'Do not auto-confirm', sub: `${critFromStages} critical issue${critFromStages === 1 ? '' : 's'} — manual review required.` }
    if (issues > 0)         return { tone: 'warn',   word: 'Needs review',       sub: `${issues} item${issues === 1 ? '' : 's'} need human verification before auto-confirm.` }
    if (v === 'looks_clean')return { tone: 'ok',     word: 'Looks clean',        sub: 'All checks passed — safe to auto-confirm.' }
    if (v === 'minor_disagreements') return { tone: 'warn', word: 'Minor disagreements', sub: 'Human review recommended.' }
    return { tone: 'danger', word: 'Do not auto-confirm', sub: 'Critical discrepancies — manual review required.' }
  }

  get matchLabel() {
    const rep = this.report
    if (rep && rep.matchLabel) return rep.matchLabel
    const m = this.match
    if (m && m.teams && m.teams.length >= 2) {
      const sc = m.score || [0, 0]
      return m.teams[0].name + ' ' + sc[0] + '–' + sc[1] + ' ' + m.teams[1].name
    }
    return ''
  }

  // ── stage data ────────────────────────────────────────────────────

  get stages() { return STAGES }

  suggestionsForStage(idx) {
    const all = (this.report && this.report.stages && this.report.stages.suggestions) || []
    return all.filter((s) => s.stage === idx && !this.dismissed[s.id])
  }

  get stageCounts() {
    const out = {}
    for (const s of STAGES) {
      const list = this.suggestionsForStage(s.idx)
      out[s.idx] = {
        total: list.length,
        critical: list.filter((x) => x.severity === 'critical').length,
        warn: list.filter((x) => x.severity === 'warn').length,
      }
    }
    return out
  }

  get totalIssues() {
    return STAGES.reduce((acc, s) => acc + this.suggestionsForStage(s.idx).length, 0)
  }

  // Stage 1 raw rows (for the diff table even when no suggestion)
  get lineupRows() {
    return (this.report && this.report.stages && this.report.stages.lineupDiffs) || []
  }
  get timelineRows() {
    return (this.report && this.report.stages && this.report.stages.timelineDiffs) || []
  }
  get anomalyRows() {
    return (this.report && this.report.stages && this.report.stages.anomalies) || []
  }
  // Stage 4 — key stats per player (shots, SoT, saves, penalties caused/won).
  // Server returns these in playerStats array. Filter to only players with any
  // value > 0 so we don't render 30+ all-zero rows.
  get keyStatsRows() {
    const ps = (this.report && this.report.playerStats) || []
    const rows = []
    for (const p of ps) {
      const shots = Number(p.shots || 0)
      const sot = Number(p.shotsOnTarget || p.sot || 0)
      const saves = Number(p.saves || p.keeperSave || 0)
      const penWon = Number(p.penaltyWon || p.penWon || 0)
      const penCaused = Number(p.penaltyCaused || p.penCaused || 0)
      if (shots || sot || saves || penWon || penCaused) {
        // Flag impossible values (Ivan #22 — negative checks).
        let flag = null
        if (shots < 0 || sot < 0 || saves < 0) flag = 'Negative value'
        else if (sot > shots) flag = 'SoT > Shots'
        rows.push({
          player: p.player || p.name || 'Unknown',
          team: p.team || '',
          shots, sot, saves, penWon, penCaused, flag,
        })
      }
    }
    return rows
  }

  // ── Stage 4: capture ──────────────────────────────────────────────

  async captureEvidence() {
    const id = this.matchId
    if (!id || this.capturing) return
    this.applyState({ capturing: true })
    try {
      const base = validatorBase()
      const res = await fetch(`${base}/capture/match/${encodeURIComponent(id)}`)
      const data = await res.json()
      this.applyState({ capturing: false, capture: data })
    } catch (e) {
      this.applyState({ capturing: false, error: (e && e.message) || 'capture failed' })
    }
  }

  downloadEvidence() {
    const blob = new Blob([JSON.stringify({
      matchId: this.matchId,
      matchLabel: this.matchLabel,
      report: this.report,
      capture: this.capture,
      exportedAt: new Date().toISOString(),
    }, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'sc-validator-' + this.matchId + '.json'
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 30000)
  }

  // Confirm hand-off — integrate with Sportyman's native flow.
  // Read-only contract: we do NOT POST to Sportyman. Instead, fire a custom
  // event that the parent confirmation-screen can listen for, and visually
  // highlight the native confirm control.
  confirmAndPost() {
    const matchId = this.matchId
    const base = validatorBase()
    this.dispatchEvent(new CustomEvent('sc:confirm-request', {
      bubbles: true, composed: true,
      detail: { matchId, evidence: this.capture },
    }))
    const now = new Date()
    const lockedAt = now.toISOString()
    // Actual lock: ask daemon to copy current shots to a locked snapshot.
    // After lock, shot URLs use lockedAt as cache key so re-runs don't
    // overwrite the captured evidence.
    if (matchId && base) {
      fetch(`${base}/lock-shots/${encodeURIComponent(matchId)}?lockedAt=${encodeURIComponent(lockedAt)}`, {
        method: 'POST',
        headers: { 'cache-control': 'no-store' },
        credentials: 'include',
      }).catch((e) => console.warn('[sc-validator] lock-shots failed', e))
    }
    // Also fire Slack confirm webhook if daemon supports it
    if (matchId && base) {
      fetch(`${base}/slack-confirm`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          matchId,
          matchLabel: (this.report && this.report.matchLabel) || '',
          confirmedBy: ((this.report && this.report.provenance && this.report.provenance.audit && this.report.provenance.audit.ranBy)) || 'operator',
        }),
        credentials: 'include',
      }).catch((e) => console.warn('[sc-validator] slack post failed', e))
    }
    this.applyState({
      confirmed: true,
      confirmedAt: now.toLocaleString('sv-SE'),
      lockedAt,
    })
  }

  copySlackConfirm() {
    const rep = this.report
    const prov = (rep && rep.provenance && rep.provenance.sportyman) || {}
    const home = prov.homeTeam
    const away = prov.awayTeam
    if (!home || !away) return
    const text = `${home} - ${away} match is AI checked & confirmed`
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(text)
        console.log('[sc-validator] copied to clipboard:', text)
      }
    } catch (e) { console.warn('[sc-validator] clipboard write failed', e) }
  }

  get hasOutstanding() {
    return this.totalIssues > 0
  }

  // 12-bead pipeline state — derived from report + stage suggestions, so
  // 'Lineup validation' lights warn when stages.lineupDiffs has entries, etc.
  // Kept compact so the bar always fits above the stage nav.
  get pipelineBeads() {
    const rep = this.report
    const stages = (rep && rep.stages) || {}
    const lineupDiffs = stages.lineupDiffs || []
    const timelineDiffs = (stages.timelineDiffs || []).filter((t) => t.match.pl !== 'match' || t.match.ws !== 'match')
    const anomalies = stages.anomalies || []
    const crit = (rep && rep.summary && rep.summary.critical) || 0
    const externalOk = rep && !rep.external_unavailable

    // Pipeline indexes:
    // 0 verify  1 scrape  2 lock  3 refresh  4 lineups  5 stats
    // 6 edge    7 anomaly 8 conflict  9 doc  10 confirm  11 log
    const opChecks = this.opChecks || {}
    const states = PIPELINE.map((s, i) => {
      if (s.actor === 'ai') {
        if (!rep) return 'pending'
        if (i === 0) return rep.matchLabel ? 'ok' : 'pending'
        if (i === 4) return lineupDiffs.length ? 'warn' : 'ok'
        if (i === 5) return externalOk ? (timelineDiffs.length ? 'warn' : 'ok') : 'pending'
        if (i === 6) return anomalies.some((a) => /sub_boundary|fantasy_assist/.test(a.rule)) ? 'warn' : 'ok'
        if (i === 7) return anomalies.length ? (crit ? 'warn' : 'ok') : 'ok'
        if (i === 8) return externalOk ? 'ok' : 'pending'
      }
      if (s.actor === 'operator' || s.actor === 'human') {
        return opChecks[i] ? 'ok' : 'pending'
      }
      return 'pending'
    })
    return PIPELINE.map((p, i) => ({ actor: p.actor, label: p.label, status: states[i] }))
  }

  get pipelineCounts() {
    const b = this.pipelineBeads
    const done = b.filter((x) => x.status === 'ok').length
    const flagged = b.filter((x) => x.status === 'warn').length
    return { done, flagged, pending: b.length - done - flagged, total: b.length }
  }

  // Click handler for operator/human beads — toggles and persists.
  toggleOp(idx) {
    const next = Object.assign({}, this.opChecks || {})
    next[idx] = !next[idx]
    if (typeof localStorage !== 'undefined' && this.matchId) {
      try { localStorage.setItem('sc-val-ops-' + this.matchId, JSON.stringify(next)) } catch (_e) {}
    }
    this.applyState({ opChecks: next })
  }

  // ── sources panel (for hover-strip) ───────────────────────────────

  get sources() {
    const rep = this.report
    if (!rep || !Array.isArray(rep.sources)) return []
    const base = validatorBase()
    return rep.sources.map((s) => ({
      ...s,
      shotUrl: s.shot ? base + s.shot : null,
      tone: /^consulted/.test(s.status || '') ? 'ok' : (s.status === 'blocked' ? 'warn' : 'muted'),
    }))
  }
}
